import { useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES } from "../data/site";
import { PageHero, Reveal, SectionTag } from "../components/Reveal";

const HOTSPOT_POS = [
  { top: "69%", left: "40%" },  // Berço 1 S
  { top: "67%", left: "53%" },  // Berço 2 N
  { top: "48%", left: "48%" },  // Pátio 01 — Grãos
  { top: "45%", left: "39%" },  // Pátio 02 — Carga Geral
  { top: "40%", left: "60%" },  // Pátio 03 — Lithothamnium
  { top: "63%", left: "57%" },  // Pátio 04 — BMO
  { top: "88%", left: "50%" },  // Canal de Acesso (centro inferior da foto)
];

const Infraestrutura = () => {
  const { t } = useLanguage();
  const p = t.infra;
  const [active, setActive] = useState(null);

  const blocks = [
    { tag: p.block1Tag, title: p.block1Title, text: p.block1Text },
    { tag: p.block2Tag, title: p.block2Title, text: p.block2Text },
    { tag: p.block3Tag, title: p.block3Title, text: p.block3Text },
  ];

  return (
    <main data-testid="infrastructure-page">
      <PageHero chip={p.chip} title={p.title} sub={p.sub} testid="infrastructure-hero" />

      {/* blocos */}
      <section className="px-6 lg:px-12 py-20 border-b border-ops">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {blocks.map((b, i) => (
            <Reveal key={b.tag} delay={i * 0.1}>
              <div data-testid={`infra-block-${i}`} className="p-8 rounded-xl border border-ops bg-ops-surface h-full space-y-4 hover:border-ops-light/60 transition-colors">
                <SectionTag>{b.tag}</SectionTag>
                <h2 className="font-display text-xl font-bold uppercase text-white leading-snug">{b.title}</h2>
                <p className="text-sm text-ops-muted leading-relaxed">{b.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* foto frontal */}
      <section className="px-6 lg:px-12 py-16 border-b border-ops bg-ops-panel/60">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="rounded-2xl overflow-hidden border border-ops aspect-[21/9]">
              <img src={IMAGES.quayVessel} alt="Vista aérea do TUP Open Port com embarcação atracada" data-testid="infra-front-image" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* aérea com hotspots */}
      <section className="px-6 lg:px-12 py-20">
        <div className="max-w-7xl mx-auto space-y-10">
          <div className="space-y-3">
            <SectionTag>{p.aerialTitle}</SectionTag>
            <p className="text-sm text-ops-muted">{p.aerialSub}</p>
          </div>

          <Reveal>
            <div data-testid="infra-aerial-map" className="relative rounded-2xl overflow-hidden border border-ops">
              <img src={IMAGES.terminalAerial} alt={p.aerialTitle} className="w-full h-auto block" />
              <div className="absolute inset-0 bg-ops-base/20" />
              {p.hotspots.map((h, i) => (
                <div key={h.label} className="absolute" style={{ top: HOTSPOT_POS[i].top, left: HOTSPOT_POS[i].left }}>
                  <button
                    data-testid={`hotspot-${i}`}
                    onClick={() => setActive(active === i ? null : i)}
                    onMouseEnter={() => setActive(i)}
                    className="relative flex items-center justify-center w-6 h-6 -translate-x-1/2 -translate-y-1/2"
                    aria-label={h.label}
                  >
                    <span className="absolute inline-flex w-full h-full rounded-full bg-ops-light opacity-75 hotspot-ping" />
                    <span className="relative inline-flex w-3.5 h-3.5 rounded-full bg-ops-light border-2 border-white" />
                  </button>
                  {active === i && (
                    <div
                      data-testid={`hotspot-tooltip-${i}`}
                      className={`absolute ${parseFloat(HOTSPOT_POS[i].left) > 55 ? "right-4" : "left-4"} ${parseFloat(HOTSPOT_POS[i].top) > 60 ? "bottom-5" : "top-0 -translate-y-1/2"} z-10 w-56 p-4 rounded-lg bg-ops-base/95 border border-ops backdrop-blur-sm`}
                    >
                      <span className="mono-tag text-[10px] text-ops-light uppercase tracking-widest block mb-1">{h.label}</span>
                      <p className="text-xs text-ops-muted leading-relaxed">{h.desc}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Reveal>

          {/* legend */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {p.hotspots.map((h, i) => (
              <button
                key={h.label}
                data-testid={`hotspot-legend-${i}`}
                onClick={() => setActive(i)}
                className="p-4 rounded-lg border border-ops bg-ops-surface text-left hover:border-ops-light/60 transition-colors"
              >
                <span className="mono-tag text-[10px] text-ops-light block mb-1">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-xs font-display font-bold uppercase text-white">{h.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Infraestrutura;
